import React, { useState, useCallback } from 'react'
import { Button, Input, message, Select, Form } from 'antd'
import { CaretDownOutlined, CloseOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar } from '@fortawesome/free-solid-svg-icons'
import { debounce } from 'lodash'

// Import necessary components and utilities
import GoogleAddressMobile from '../../reusable-components/GoogleAddressMobile'
import DatePicker from '../../Patient/Reusable Components/DatePicker'
import AgiliteSkeleton from '../../reusable-components/AgiliteSkeleton'
import {
  MedPraxSchemeSearch,
  MedPraxSchemePlanSearch,
  MedPraxSchemePlanOptionSearch
} from '../../Scripting/utils/utils'
import { handleError } from '../../lib/utils'
import { updateAgiliteUser } from '../../Auth/utils/utils'
import { executeBooking, getBookingState } from '../../Bookings/utils/bpm-utils'
import { updateBooking } from '../../Bookings/utils/utils'

const BasicInfo = ({ formData, setFormData, setCurrentStep, form, pickerValue, setPickerValue, bookingData }) => {
  // Medical Aid state
  const [schemeLoading, setSchemeLoading] = useState(false)
  const [schemeData, setSchemeData] = useState([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [plansData, setPlansData] = useState([])
  const [planOptionsLoading, setPlanOptionsLoading] = useState(false)
  const [planOptionData, setPlanOptionData] = useState([])
  const [showDatePicker, setShowDatePicker] = useState(false)

  // Medical Aid API functions
  const handleGetMedicalAids = async (query) => {
    let tmpData = []
    setSchemeLoading(true)
    try {
      if (query) {
        tmpData = await MedPraxSchemeSearch(query)
        setSchemeData(tmpData)
      } else {
        setSchemeData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }
    setSchemeLoading(false)
  }

  const handleGetMedicalAidPlans = async (schemeCode) => {
    let tmpData = []
    setPlansLoading(true)
    try {
      if (schemeCode) {
        tmpData = await MedPraxSchemePlanSearch(schemeCode)
        setPlansData(tmpData)
      } else {
        setPlansData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }
    setPlansLoading(false)
  }

  const handleGetMedicalAidPlanOptions = async (planCode) => {
    let tmpData = []
    setPlanOptionsLoading(true)
    try {
      if (planCode) {
        tmpData = await MedPraxSchemePlanOptionSearch(planCode)
        setPlanOptionData(tmpData)
      } else {
        setPlanOptionData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }
    setPlanOptionsLoading(false)
  }

  // Debounced scheme search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSchemeFilter = useCallback(
    debounce((query) => {
      if (query) {
        handleGetMedicalAids(query)
      } else {
        setSchemeLoading(false)
      }
    }, 1000),
    []
  )

  // Date picker handlers
  const handleDateSelect = (val) => {
    setPickerValue(val)
    form.setFieldValue('dateOfBirth', val)
    handleBasicInfoChange('dateOfBirth', val)
    setShowDatePicker(false)
  }

  const handleBasicInfoChange = (field, value) => {
    setFormData({
      ...formData,
      basicInfo: { ...formData.basicInfo, [field]: value }
    })
  }

  const handleCompleteCheckin = async () => {
    try {
      await updateAgiliteUser(formData.patientRecord._id, formData.basicInfo)
      let processRef = await getBookingState([bookingData.processRef])
      let processRecord = await executeBooking(
        'patient_journey',
        bookingData.processRef,
        'submit',
        `${formData.patientRecord.firstName} ${formData.patientRecord.lastName}`,
        processRef.key,
        'screening'
      )
      await updateBooking({ status: processRecord.key }, { _id: bookingData._id })
      setCurrentStep(5)
    } catch (error) {
      message.error(handleError(error, true))
    }
  }

  return (
    <div className='step-container'>
      <h2>Confirm Basic Information</h2>
      <p>Please confirm your details and medical aid information</p>
      <Form
        form={form}
        layout='vertical'
        onFinish={(values) => {
          handleCompleteCheckin()
        }}
        initialValues={{
          firstName: formData.basicInfo.firstName,
          lastName: formData.basicInfo.lastName,
          phoneNumber: formData.basicInfo.phoneNumber,
          email: formData.basicInfo.email,
          physicalAddress: formData.basicInfo.physicalAddress,
          dateOfBirth: formData.basicInfo.dateOfBirth,
          medicalAid: formData.basicInfo.medicalAid
        }}
      >
        {/* Hidden fields for medical aid codes */}
        <Form.Item name={['medicalAid', 'schemeCode']} style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name={['medicalAid', 'planCode']} style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name={['medicalAid', 'planOptionCode']} style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <div className='basic-info-form'>
          <Form.Item
            label='First Name'
            name='firstName'
            rules={[{ required: true, message: 'Please enter your first name' }]}
          >
            <Input
              placeholder='Enter your first name'
              onChange={(e) => handleBasicInfoChange('firstName', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label='Last Name'
            name='lastName'
            rules={[{ required: true, message: 'Please enter your last name' }]}
          >
            <Input
              placeholder='Enter your last name'
              onChange={(e) => handleBasicInfoChange('lastName', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label='Phone Number'
            name='phoneNumber'
            rules={[{ required: true, message: 'Please enter your phone number' }]}
          >
            <Input
              type='tel'
              placeholder='Enter your phone number'
              onChange={(e) => handleBasicInfoChange('phoneNumber', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label='Email Address'
            name='email'
            rules={[
              { required: true, message: 'Please enter your email address' },
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input
              type='email'
              placeholder='Enter your email address'
              onChange={(e) => handleBasicInfoChange('email', e.target.value)}
            />
          </Form.Item>

          <Form.Item label='Date Of Birth' name='dateOfBirth'>
            <div onClick={() => setShowDatePicker(true)}>
              <Input
                placeholder='Select date of birth'
                value={pickerValue ? pickerValue.format('DD/MM/YYYY') : ''}
                readOnly
                suffix={<FontAwesomeIcon icon={faCalendar} style={{ color: '#1976d2' }} />}
              />
            </div>
            <DatePicker
              showDatePicker={showDatePicker}
              setShowDatePicker={setShowDatePicker}
              pickerValue={pickerValue}
              setPickerValue={setPickerValue}
              handleDateSelect={handleDateSelect}
              allowPastDates={true}
              showQuickSelect={false}
              confirmText='Confirm'
              cancelText='Cancel'
            />
          </Form.Item>

          <Form.Item label='Physical Address' name='physicalAddress'>
            <GoogleAddressMobile
              selectProps={{
                placeholder: form.getFieldValue('physicalAddress') || 'Enter your physical address',
                onChange: (e) => {
                  form.setFieldValue('physicalAddress', e.label)
                  handleBasicInfoChange('physicalAddress', e.label)
                }
              }}
            />
          </Form.Item>

          {/* Medical Aid Section */}
          <div
            className='medical-aid-section'
            style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}
          >
            <h3 style={{ marginBottom: '1rem' }}>Medical Aid Details</h3>

            <Form.Item label='Medical Aid Name' name={['medicalAid', 'name']}>
              <Select
                suffixIcon={<CaretDownOutlined />}
                clearIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
                showSearch
                allowClear
                placeholder='Search for a Medical Aid...'
                onSearch={(value) => {
                  debouncedSchemeFilter(value)
                }}
                onChange={(e, option) => {
                  // Clear dependent fields when medical aid name changes
                  form.setFieldValue(['medicalAid', 'schemeCode'], option?.data ? JSON.parse(option.data).code : '')
                  form.setFieldValue(['medicalAid', 'plan'], undefined)
                  form.setFieldValue(['medicalAid', 'planCode'], '')
                  form.setFieldValue(['medicalAid', 'planOption'], undefined)
                  form.setFieldValue(['medicalAid', 'planOptionCode'], '')

                  const newMedicalAid = {
                    ...formData.basicInfo.medicalAid,
                    name: e,
                    schemeCode: option?.data ? JSON.parse(option.data).code : '',
                    plan: '',
                    planCode: '',
                    planOption: '',
                    planOptionCode: ''
                  }
                  handleBasicInfoChange('medicalAid', newMedicalAid)

                  // Clear state arrays
                  setSchemeData([])
                  setPlansData([])
                  setPlanOptionData([])
                }}
                options={schemeData.map((i) => ({
                  label: i.name,
                  value: i.name,
                  data: JSON.stringify(i)
                }))}
                notFoundContent={
                  schemeLoading ? (
                    <AgiliteSkeleton skActive spinnerTip='Searching Schemes...' />
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <div>No results found please adjust your search</div>
                    </div>
                  )
                }
              />
            </Form.Item>

            <Form.Item label='Medical Aid Plan' name={['medicalAid', 'plan']}>
              <Select
                suffixIcon={<CaretDownOutlined />}
                clearIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
                showSearch
                allowClear
                onClear={() => {
                  form.setFieldValue(['medicalAid', 'planCode'], '')
                  form.setFieldValue(['medicalAid', 'planOption'], undefined)
                  form.setFieldValue(['medicalAid', 'planOptionCode'], '')
                }}
                placeholder='Select a Medical Aid Plan...'
                onChange={(e, option) => {
                  // Clear dependent fields when medical aid plan changes
                  form.setFieldValue(['medicalAid', 'planCode'], option?.data ? JSON.parse(option.data).code : '')
                  form.setFieldValue(['medicalAid', 'planOption'], undefined)
                  form.setFieldValue(['medicalAid', 'planOptionCode'], '')

                  const newMedicalAid = {
                    ...formData.basicInfo.medicalAid,
                    plan: e,
                    planCode: option?.data ? JSON.parse(option.data).code : '',
                    planOption: '',
                    planOptionCode: ''
                  }
                  handleBasicInfoChange('medicalAid', newMedicalAid)

                  // Clear state arrays
                  setPlansData([])
                  setPlanOptionData([])
                }}
                onClick={() => {
                  handleGetMedicalAidPlans(form.getFieldValue(['medicalAid', 'schemeCode']))
                }}
                options={plansData.map((i) => ({
                  label: i.option,
                  value: i.option,
                  data: JSON.stringify(i)
                }))}
                notFoundContent={
                  plansLoading ? (
                    <AgiliteSkeleton skActive spinnerTip='Searching Scheme Plans...' />
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <div>No results found please adjust your search</div>
                    </div>
                  )
                }
              />
            </Form.Item>

            <Form.Item label='Medical Aid Plan Option' name={['medicalAid', 'planOption']}>
              <Select
                suffixIcon={<CaretDownOutlined />}
                clearIcon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
                showSearch
                allowClear
                onClear={() => {
                  form.setFieldValue(['medicalAid', 'planOptionCode'], '')
                }}
                placeholder='Select a Medical Aid Plan Option...'
                onChange={(e, option) => {
                  form.setFieldValue(['medicalAid', 'planOptionCode'], option?.data ? JSON.parse(option.data).code : '')
                  const newMedicalAid = {
                    ...formData.basicInfo.medicalAid,
                    planOption: e,
                    planOptionCode: option?.data ? JSON.parse(option.data).code : ''
                  }
                  handleBasicInfoChange('medicalAid', newMedicalAid)
                  setPlanOptionData([])
                }}
                onClick={() => {
                  handleGetMedicalAidPlanOptions(form.getFieldValue(['medicalAid', 'planCode']))
                }}
                options={planOptionData.map((i) => ({
                  label: i.option,
                  value: i.option,
                  data: JSON.stringify(i)
                }))}
                notFoundContent={
                  planOptionsLoading ? (
                    <AgiliteSkeleton skActive spinnerTip='Searching Scheme Plan Options...' />
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <div>No results found please adjust your search</div>
                    </div>
                  )
                }
              />
            </Form.Item>

            <Form.Item label='Medical Aid Number' name={['medicalAid', 'number']}>
              <Input
                placeholder='Enter your medical aid number'
                onChange={(e) => {
                  const newMedicalAid = { ...formData.basicInfo.medicalAid, number: e.target.value }
                  handleBasicInfoChange('medicalAid', newMedicalAid)
                }}
              />
            </Form.Item>

            <Form.Item label='Dependant Number' name={['medicalAid', 'dependantNumber']}>
              <Input
                placeholder='Enter your dependant number'
                onChange={(e) => {
                  const newMedicalAid = { ...formData.basicInfo.medicalAid, dependantNumber: e.target.value }
                  handleBasicInfoChange('medicalAid', newMedicalAid)
                }}
              />
            </Form.Item>
          </div>
        </div>

        <Button type='primary' htmlType='submit' style={{ marginTop: '1rem' }}>
          Complete Check-in
        </Button>
      </Form>
    </div>
  )
}

export default BasicInfo
